import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Copy, Check, ShieldCheck, Landmark, ArrowRight, Sparkles, Truck } from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";
import { toast } from "sonner";
import api from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { ProductImage } from "../components/ProductImage";

const Row = ({ label, value, mono = false, copyable = false, highlight = false, testid }) => {
    const [copied, setCopied] = useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(String(value));
            setCopied(true);
            toast.success(`${label} copied`);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Could not copy");
        }
    };
    return (
        <div className="flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-0">
            <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</span>
            <div className="flex items-center gap-2">
                <span
                    className={`text-right text-sm ${mono ? "font-mono" : ""} ${highlight ? "text-[var(--gold)] font-bold tracking-wide" : "text-white font-semibold"}`}
                    data-testid={testid}
                >
                    {value}
                </span>
                {copyable && (
                    <button
                        type="button"
                        onClick={onCopy}
                        className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--gold)] hover:bg-white/5"
                        aria-label={`Copy ${label}`}
                        data-testid={`copy-${(testid || label).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                )}
            </div>
        </div>
    );
};

export const OrderInstructionsPage = () => {
    const { reference } = useParams();
    const [order, setOrder] = useState(null);
    const [err, setErr] = useState(null);
    useSEO({
        title: `Order ${reference} — Payment Instructions · Carbon Labs AU`,
        description: "Complete your Carbon Labs research peptide order.",
    });

    useEffect(() => {
        api.get(`/orders/${reference}`)
            .then((r) => setOrder(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Order not found"));
    }, [reference]);

    if (err) {
        return (
            <div className="pt-32 pb-20 min-h-screen relative">
                <MolecularBg intensity="low" />
                <div className="container-x max-w-2xl text-center relative z-10">
                    <h1 className="font-display text-3xl font-bold text-white mb-4">Order not found</h1>
                    <p className="text-[var(--text-secondary)] mb-6">{err}</p>
                    <Link to="/shop" className="btn-solid">
                        Back to shop <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        );
    }
    if (!order) {
        return (
            <div className="pt-32 pb-20 min-h-screen flex items-center justify-center">
                <div className="text-[var(--text-muted)] font-mono text-sm">Loading order…</div>
            </div>
        );
    }

    const inst = order.instructions || {};
    const isPaypal = order.provider === "paypal";
    const isStripe = order.provider === "stripe" || order.provider === "bnpl";
    const awaiting = order.payment_status === "awaiting_payment";
    const paid = order.payment_status === "paid";
    // A manual (admin-managed) payment method — anything that isn't stripe/paypal/bnpl.
    const isManual = !isStripe && !isPaypal && !!order.provider;
    const instructionsHidden = !!inst.hidden;
    const paymentDetailEntries = Object.entries(inst.payment_details || {}).filter(
        ([, v]) => v !== undefined && v !== null && String(v).trim() !== "",
    );

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="order-instructions-page">
            <MolecularBg intensity="low" />
            <div className="container-x max-w-3xl relative z-10">
                <span className="eyebrow">// Order confirmed</span>
                <h1 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-3">
                    {paid ? "Payment received — order in dispatch queue" : "Complete your payment"}
                </h1>
                <p className="text-[var(--text-secondary)] mb-8">
                    {paid
                        ? "Thank you. Your research peptides are reserved and will be dispatched cold-chain within one business day."
                        : "Your order is reserved. Once we receive your transfer we'll dispatch the same business day."}
                </p>

                {/* Order summary */}
                <div className="glass-elevated rounded-2xl p-6 mb-6">
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] mb-3">
                        Order details
                    </p>
                    <Row label="Reference" value={order.reference} mono copyable highlight testid="order-reference" />
                    <Row label="Amount" value={`A$${order.amount.toFixed(2)} AUD`} mono testid="order-amount" />
                    <Row
                        label="Status"
                        value={
                            paid
                                ? "Paid — preparing dispatch"
                                : awaiting
                                  ? "Awaiting payment"
                                  : order.payment_status
                        }
                        testid="order-status"
                    />
                    <Row label="Method" value={inst.method || order.provider} testid="order-method" />
                </div>

                {/* Shipping address confirmation */}
                {order.shipping_address && order.shipping_address.line1 && (
                    <div className="glass-elevated rounded-2xl p-6 mb-6" data-testid="order-shipping-address">
                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] mb-3">
                            Shipping to
                        </p>
                        <div className="text-sm text-white leading-relaxed">
                            <div className="font-display font-bold text-base">{order.shipping_address.full_name}</div>
                            <div>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</div>
                            <div>{order.shipping_address.suburb} {order.shipping_address.state} {order.shipping_address.postcode}</div>
                            <div>{order.shipping_address.country}</div>
                            {order.shipping_address.phone && (
                                <div className="text-[var(--text-secondary)] mt-1">Phone: {order.shipping_address.phone}</div>
                            )}
                        </div>
                        {order.shipping_method && order.shipping_method.name && (
                            <div className="mt-4 pt-4 border-t border-white/5 flex items-start gap-2" data-testid="order-shipping-method">
                                <Truck size={14} className="text-[var(--gold)] mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                        Shipping method
                                    </div>
                                    <div className="text-sm text-white font-semibold" data-testid="order-shipping-method-name">
                                        {order.shipping_method.name}
                                        <span className="text-[var(--text-muted)] font-mono ml-2 text-xs">
                                            {Number(order.shipping_cost ?? order.shipping_method.price ?? 0) > 0
                                                ? `A$${Number(order.shipping_cost ?? order.shipping_method.price).toFixed(2)}`
                                                : "Free"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Payment instructions for manual (admin-managed) methods */}
                {isManual && awaiting && !instructionsHidden && (
                    <div className="glass-elevated rounded-2xl p-6 mb-6 border border-[var(--gold)]/30" data-testid="manual-payment-instructions">
                        <div className="flex items-center gap-3 mb-4">
                            <Landmark size={18} className="text-[var(--gold)]" />
                            <h2 className="font-display text-xl font-bold text-white uppercase tracking-wider">
                                {inst.method ? `Send via ${inst.method}` : "Complete payment"}
                            </h2>
                        </div>

                        {inst.instructions_body && (
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
                                {inst.instructions_body}
                            </p>
                        )}

                        {paymentDetailEntries.map(([k, v]) => (
                            <Row
                                key={k}
                                label={k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                value={String(v)}
                                mono
                                copyable
                                highlight
                                testid={`pd-${k}`}
                            />
                        ))}

                        <Row
                            label="Amount"
                            value={`A$${order.amount.toFixed(2)}`}
                            mono
                            copyable
                            highlight
                            testid="instruction-amount"
                        />
                        <Row
                            label="Payment description"
                            value={order.reference}
                            mono
                            copyable
                            highlight
                            testid="instruction-reference"
                        />

                        {inst.reference_instructions ? (
                            <div className="mt-5 p-4 rounded-xl border border-[var(--gold)]/30 bg-[rgba(233,188,11,0.06)]">
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                                    <strong className="text-white">Important:</strong> {inst.reference_instructions}
                                </p>
                            </div>
                        ) : (
                            <div className="mt-5 p-4 rounded-xl border border-[var(--gold)]/30 bg-[rgba(233,188,11,0.06)]">
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                                    <strong className="text-white">Important:</strong> Add{" "}
                                    <span className="text-[var(--gold)] font-bold font-mono">{order.reference}</span>{" "}
                                    as the payment description so we can match your transfer.
                                </p>
                            </div>
                        )}

                        <p className="mt-4 text-xs text-[var(--text-muted)] font-mono uppercase tracking-widest">
                            We&apos;ll email you the moment funds are received.
                        </p>
                    </div>
                )}

                {/* Instructions redacted — payment method has been disabled by admin. */}
                {isManual && awaiting && instructionsHidden && (
                    <div className="glass-elevated rounded-2xl p-6 mb-6" data-testid="manual-payment-hidden">
                        <div className="flex items-center gap-3 mb-3">
                            <ShieldCheck className="text-[var(--gold)]" size={20} />
                            <h2 className="font-display text-xl font-bold text-white">Payment instructions unavailable</h2>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                            {inst.note || "Payment instructions are temporarily unavailable. Please contact support if you need assistance completing this order."}
                        </p>
                    </div>
                )}

                {/* Generic confirmation for instant methods */}
                {(isPaypal || isStripe) && paid && (
                    <div className="glass-elevated rounded-2xl p-6 mb-6">
                        <div className="flex items-center gap-3 mb-3">
                            <ShieldCheck className="text-emerald-400" size={20} />
                            <h2 className="font-display text-xl font-bold text-white">Payment confirmed</h2>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                            Your {isPaypal ? "PayPal" : "card"} payment was captured successfully. A receipt has
                            been sent to your email. Dispatching cold-chain from Sydney within one business day.
                        </p>
                    </div>
                )}

                <OrderUpsellRow reference={reference} />

                <div className="flex flex-wrap gap-3">
                    <Link to="/shop" className="btn-ghost" data-testid="back-to-shop">
                        Continue shopping <ArrowRight size={14} />
                    </Link>
                    <a
                        href="mailto:carbonlabsau@outlook.com"
                        className="btn-solid"
                        data-testid="contact-support"
                    >
                        Contact support
                    </a>
                </div>
            </div>
        </div>
    );
};

const OrderUpsellRow = ({ reference }) => {
    const [items, setItems] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/orders/${reference}/upsells`, { params: { limit: 3 } });
                if (!cancelled) setItems(Array.isArray(data) ? data : []);
            } catch {
                if (!cancelled) setItems([]);
            }
        })();
        return () => { cancelled = true; };
    }, [reference]);

    if (items.length === 0) return null;
    return (
        <div className="glass rounded-2xl p-5 mb-6" data-testid="order-upsell-row">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[var(--gold)]" />
                <span className="eyebrow text-[var(--gold)]">// Researchers also stacked</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {items.slice(0, 3).map((p) => {
                    const onSale = p.sale_price != null && p.sale_price < p.price;
                    const price = p.sale_price ?? p.price;
                    return (
                        <Link
                            key={p.slug}
                            to={`/product/${p.slug}`}
                            className="glass rounded-xl p-3 border border-white/5 hover:border-[var(--gold)]/40 transition-colors group block"
                            data-testid={`order-upsell-${p.slug}`}
                        >
                            <ProductImage product={p} ratio="3 / 2" className="rounded-lg mb-2" />
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{p.dosage}</div>
                            <div className="text-sm font-semibold text-white truncate group-hover:text-[var(--gold)] transition-colors">{p.name}</div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className="text-base font-bold text-[var(--gold)]">A${Number(price).toFixed(2)}</span>
                                {onSale && (
                                    <span className="text-xs text-[var(--text-muted)] line-through">A${Number(p.price).toFixed(2)}</span>
                                )}
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};
